import React from 'react';
import { GameState, Investigator, UserAction } from '../api';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { findInvestigator } from '../utils/investigators';
import { useMonsterTokenStyle } from '../utils/clientSettings';

type PlayerHeaderProps = {
  gameState: GameState;
  investigator?: Investigator;
}
export const PlayerHeader: React.FC<PlayerHeaderProps> = ({ gameState, investigator }) => {
  const digital = useMonsterTokenStyle() === 'Digital';
  if (!investigator || digital) {
    return null;
  }
  const players = gameState.players ?? [];
  const owner =
    players.find(p =>
      (p.controlledInvestigators || []).some(name => name.toLowerCase() === investigator.name.toLowerCase())
    )?.name;
  let isOnline = gameState.playersDetailed?.find((p) => p.name === owner)?.isOnline;
  const isSolo = !!gameState.firstInvestigator;
  const isFirst = isSolo ? investigator.name === gameState.firstInvestigator : owner === gameState.firstPlayer;
  return (
    <div className="player-header">
      {investigator.name}
      {owner && <span className="owner-badge"> ({owner})</span>}
      {isFirst && <span className="first-player-badge"> First</span>}
      <div className='online-status' style={{
        background: isOnline ? 'green' : 'red'
      }} />
    </div>
  )
}

interface PlayersListProps {
  gameState: GameState;
  availableActions?: UserAction[];
  onExecuteAction?: (actionId: string) => void;
  currentPlayerName?: string;
  language?: string;
  investigatorToShow?: Investigator;
}

export const PlayersList: React.FC<PlayersListProps> = ({
  gameState,
  availableActions,
  onExecuteAction,
  currentPlayerName,
  language,
  investigatorToShow,
}) => {
  const players = gameState.players ?? [];
  const playersOrder = gameState.playersOrder ?? [];
  const ownerByInvestigator: Record<string, string> = {};

  players.forEach(player => {
    (player.controlledInvestigators || []).forEach(name => {
      if (name) {
        ownerByInvestigator[name] = player.name;
      }
    });
  });

  const baseTurnOrder = (
    gameState.investigatorTurnOrder && gameState.investigatorTurnOrder.length > 0
      ? gameState.investigatorTurnOrder
      : playersOrder.flatMap(playerName => {
          const controlled =
            players.find(p => p.name === playerName)?.controlledInvestigators || [];
          return controlled;
        })
  ).filter((name, index, arr) =>
    !!name && arr.findIndex(other => other.toLowerCase() === name.toLowerCase()) === index
  );

  const turnOrder =
    investigatorToShow && investigatorToShow.name && baseTurnOrder.includes(investigatorToShow.name)
      ? [
          ...baseTurnOrder.slice(baseTurnOrder.indexOf(investigatorToShow.name)),
          ...baseTurnOrder.slice(0, baseTurnOrder.indexOf(investigatorToShow.name)),
        ]
      : baseTurnOrder;

  const investigatorEntries = turnOrder.map(name => {
    const owner = ownerByInvestigator[name];
    const investigator = owner ? findInvestigator(gameState, name) : undefined;
    let isOnline = gameState.playersDetailed?.find((p) => p.name === owner)?.isOnline;
    
    return { investigator, investigatorName: name, owner, isOnline };
  });

  const actionWithInvestigatorName = (availableActions || []).find((a) => !!a.investigatorName);

  return (
    <div className="game-details">
      <div className="players-order">
        {investigatorEntries.length === 0 && <div className="player-info">None yet</div>}
        {investigatorEntries.map((entry, idx) => (
          <div key={`${entry.investigatorName}-${idx}`} className="player-info">
            <PlayerHeader gameState={gameState} investigator={entry.investigator} />
            {entry.investigator && (
              <InvestigatorDisplay gameState={gameState}
                investigator={entry.investigator}
                availableActions={!!actionWithInvestigatorName && actionWithInvestigatorName.investigatorName === entry.investigator.name ? availableActions : undefined}
                onExecuteAction={onExecuteAction}
                showStats
                deckInfo={{
                  commonItems: gameState.decks?.commonItems,
                  uniqueItems: gameState.decks?.uniqueItems,
                  spells: gameState.decks?.spells,
                }}
                language={language}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
